import React, { Component } from 'react';

interface Props {
	children: ((params: { referenceRect: DOMRect }) => React.ReactNode) | React.ReactNode;
	resolveReference: () => Element | null | Text;
}

interface State {
	referenceRect: DOMRect | null;
}

export default class LayoutConstraintContent extends Component<Props, State> {
	state: State = { referenceRect: null };

	mounted = true;

	componentDidMount() {
		this.mounted = true;
		this.updateRect(this.props);
		window.addEventListener('resize', this.handleWindowEvent, {
			passive: true,
		});
		window.addEventListener('scroll', this.handleWindowEvent, {
			capture: true,
			passive: true,
		});
	}

	componentWillUnmount() {
		this.mounted = false;
		window.removeEventListener('resize', this.handleWindowEvent);
		window.removeEventListener('scroll', this.handleWindowEvent, {
			capture: true,
		});
	}

	componentDidUpdate(prevProps: Record<never, never>) {
		if (this.props !== prevProps) {
			this.updateRect(this.props);
		}
	}

	handleWindowEvent = () => {
		this.updateRect(this.props);
	};

	updateRect(props: Props) {
		window.requestAnimationFrame(() => {
			const referenceEl = props.resolveReference();
			if (this.mounted && referenceEl) {
				const referenceRect = 'getBoundingClientRect' in referenceEl ? referenceEl.getBoundingClientRect() : null;

				if (
					this.state.referenceRect === null ||
					referenceRect?.left !== this.state.referenceRect?.left ||
					referenceRect?.top !== this.state.referenceRect?.top ||
					referenceRect?.width !== this.state.referenceRect?.width ||
					referenceRect?.height !== this.state.referenceRect?.height
				) {
					this.setState({ referenceRect });
				}
			}
		});
	}

	render() {
		const { children } = this.props;

		const { referenceRect } = this.state;

		if (!referenceRect) {
			return null;
		}

		const result =
			typeof children === 'function'
				? children({ referenceRect })
				: React.cloneElement(children as React.ReactElement, { referenceRect });

		return React.isValidElement(result) ? result : null;
	}
}
